import React from "react";
import {
  AlertCircle,
  Asterisk,
  ChevronsDownUp,
  ChevronsUpDown,
  Search,
  Store,
} from "lucide-react";

import { Button, Input, Select, Tip } from "@/components/ui";
import { useFailedLeafSaves } from "@/hooks/useLeafFlagSaves";
import { cn } from "@/lib/utils";

interface LeavesToolbarProps {
  text: string;
  onTextChange: (value: string) => void;
  required: boolean;
  onRequiredChange: (on: boolean) => void;
  perStore: boolean;
  onPerStoreChange: (on: boolean) => void;
  matchAll: boolean;
  onMatchAllChange: (all: boolean) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onShowFailed: (path: string) => void;
  count?: string;
}

const pressedClass =
  "border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300";

const IconButton: React.FC<{
  label: string;
  tip: string;
  icon: React.ElementType;
  pressed?: boolean;
  onClick: () => void;
}> = ({ label, tip, icon: Icon, pressed, onClick }) => (
  <Tip label={tip}>
    <Button
      type="button"
      variant="outline"
      size="icon"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      className={cn("shrink-0", pressed && pressedClass)}
    >
      <Icon className="h-4 w-4" />
    </Button>
  </Tip>
);

export const LeavesToolbar: React.FC<LeavesToolbarProps> = ({
  text,
  onTextChange,
  required,
  onRequiredChange,
  perStore,
  onPerStoreChange,
  matchAll,
  onMatchAllChange,
  onExpandAll,
  onCollapseAll,
  onShowFailed,
  count,
}) => {
  const failed = useFailedLeafSaves();
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="relative w-56 shrink-0 min-[1400px]:w-80">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          id="leaves-search"
          aria-label="Search leaves"
          placeholder="Search leaves"
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          className="pl-9 pr-9"
        />
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-slate-200 px-1.5 text-xs text-slate-400 dark:border-slate-700">
          /
        </kbd>
      </div>
      <IconButton
        label="Filter: Required"
        tip="Show only Required leaves"
        icon={Asterisk}
        pressed={required}
        onClick={() => onRequiredChange(!required)}
      />
      <IconButton
        label="Filter: Per store"
        tip="Show only Per store leaves"
        icon={Store}
        pressed={perStore}
        onClick={() => onPerStoreChange(!perStore)}
      />
      {required && perStore && (
        <Select
          aria-label="Match"
          value={matchAll ? "all" : "any"}
          onChange={(e) => onMatchAllChange(e.target.value === "all")}
          className="w-32 shrink-0"
        >
          <option value="all">Match all</option>
          <option value="any">Match any</option>
        </Select>
      )}
      <span className="mx-1 h-5 shrink-0 w-px bg-slate-200 dark:bg-slate-700" />
      <IconButton
        label="Expand all folders"
        tip="Expand all folders"
        icon={ChevronsUpDown}
        onClick={onExpandAll}
      />
      <IconButton
        label="Collapse all folders"
        tip="Collapse all folders"
        icon={ChevronsDownUp}
        onClick={onCollapseAll}
      />
      <div className="ml-auto flex items-center gap-3">
        {failed.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-sm font-medium text-rose-500">
              <AlertCircle className="h-4 w-4" />
              {failed.length} not saved
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onShowFailed(failed[0].path)}
            >
              Show it
            </Button>
          </div>
        )}
        {count && (
          <p className="whitespace-nowrap text-sm tabular-nums text-slate-500 dark:text-slate-400">
            {count}
          </p>
        )}
      </div>
    </div>
  );
};
